import * as THREE from 'three';
import { VOXEL_SIZE } from './VoxelInstanceBuffer';

export class PetalParticleSystem {
  private particles: {
    position: THREE.Vector3;
    rotation: THREE.Euler;
    rotationSpeed: THREE.Vector3;
    scale: THREE.Vector3;
    speedY: number;
    speedX: number;
    speedZ: number;
    swayOffset: number;
    maxLife: number;
    life: number;
    color: number;
  }[] = [];
  
  private instancedMesh: THREE.InstancedMesh;
  private maxParticles = 300;
  private dummy = new THREE.Object3D();
  private colors = [0xffccd5, 0xffb7c5, 0xff99ac, 0xffe3e8]; // beautiful shades of cherry blossom pink

  constructor(scene: THREE.Scene) {
    // Petals are tiny voxel cubes
    const geometry = new THREE.BoxGeometry(VOXEL_SIZE * 0.4, VOXEL_SIZE * 0.4, VOXEL_SIZE * 0.4);
    
    // Create a material that allows vertex colors or is just standard
    const material = new THREE.MeshStandardMaterial({
      roughness: 0.8,
      metalness: 0.1,
      transparent: true,
      opacity: 0.85,
    });

    this.instancedMesh = new THREE.InstancedMesh(geometry, material, this.maxParticles);
    this.instancedMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.instancedMesh.castShadow = true;
    this.instancedMesh.receiveShadow = false;
    // three.js caches an InstancedMesh bounding sphere from the instance matrices the first time
    // it renders, and setMatrixAt never invalidates it. Every petal is parked at y = -999 at that
    // point, so the cached sphere sits far below the world and the system is culled for good.
    this.instancedMesh.frustumCulled = false;

    // Initialize all particles offscreen
    for (let i = 0; i < this.maxParticles; i++) {
      this.dummy.position.set(0, -999, 0);
      this.dummy.scale.set(0, 0, 0);
      this.dummy.updateMatrix();
      this.instancedMesh.setMatrixAt(i, this.dummy.matrix);
    }
    this.instancedMesh.instanceMatrix.needsUpdate = true;

    scene.add(this.instancedMesh);
  }

  public update(cherryTreePositions: { x: number; y: number; z: number }[], delta: number) {
    if (cherryTreePositions.length === 0) {
      // If no cherry trees are in the world, make sure we hide all petals
      for (let i = 0; i < this.maxParticles; i++) {
        this.dummy.position.set(0, -999, 0);
        this.dummy.scale.set(0, 0, 0);
        this.dummy.updateMatrix();
        this.instancedMesh.setMatrixAt(i, this.dummy.matrix);
      }
      this.instancedMesh.instanceMatrix.needsUpdate = true;
      return;
    }

    // Spawn new falling petals under the cherry trees
    if (this.particles.length < this.maxParticles && Math.random() < 0.4) {
      const tree = cherryTreePositions[Math.floor(Math.random() * cherryTreePositions.length)]!;
      
      // Spawn within the cherry tree's canopy volume
      const rx = (Math.random() - 0.5) * 8.5; // canopy spread
      const rz = (Math.random() - 0.5) * 8.5;
      const ry = 6.0 + Math.random() * 6.5; // height within the canopy (trunkHeight=11)

      const startPos = new THREE.Vector3(
        (tree.x + rx) * VOXEL_SIZE,
        (tree.y + ry) * VOXEL_SIZE,
        (tree.z + rz) * VOXEL_SIZE
      );

      const petalColor = this.colors[Math.floor(Math.random() * this.colors.length)]!;

      this.particles.push({
        position: startPos,
        rotation: new THREE.Euler(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI),
        rotationSpeed: new THREE.Vector3(
          (Math.random() - 0.5) * 4.0,
          (Math.random() - 0.5) * 3.0,
          (Math.random() - 0.5) * 4.0
        ),
        scale: new THREE.Vector3(
          0.6 + Math.random() * 0.5,
          0.6 + Math.random() * 0.4,
          0.6 + Math.random() * 0.5
        ),
        speedY: (0.8 + Math.random() * 0.9) * VOXEL_SIZE, // gentle falling speed
        speedX: (Math.random() - 0.5) * 0.5 * VOXEL_SIZE,
        speedZ: (Math.random() - 0.5) * 0.5 * VOXEL_SIZE,
        swayOffset: Math.random() * Math.PI * 2,
        maxLife: 4.5 + Math.random() * 3.0,
        life: 0,
        color: petalColor
      });
    }

    // Update active petals
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]!;
      p.life += delta;

      const progress = p.life / p.maxLife;

      // Fall downwards with wind drift
      p.position.y -= p.speedY * delta;
      
      // Gentle floating sway (sinusoidal) on X and Z axes to look like fluttering petals
      p.position.x += (p.speedX + Math.sin(p.life * 2.5 + p.swayOffset) * 0.3 * VOXEL_SIZE) * delta;
      p.position.z += (p.speedZ + Math.cos(p.life * 2.0 + p.swayOffset) * 0.3 * VOXEL_SIZE) * delta;

      // Rotate/tumble the petals
      p.rotation.x += p.rotationSpeed.x * delta;
      p.rotation.y += p.rotationSpeed.y * delta;
      p.rotation.z += p.rotationSpeed.z * delta;

      // Fade/scale down slightly near the end of life or if they hit ground level
      // Assume ground is around y = 1.0 to 2.5
      const groundMinY = 1.5 * VOXEL_SIZE;
      const nearGround = p.position.y < groundMinY;

      if (progress >= 1.0 || nearGround) {
        this.particles.splice(i, 1);
        continue;
      }

      // Calculate scale (taper out at end of life)
      const scaleFactor = progress > 0.8 ? (1.0 - progress) / 0.2 : 1.0;
      this.dummy.position.copy(p.position);
      this.dummy.rotation.copy(p.rotation);
      this.dummy.scale.copy(p.scale).multiplyScalar(scaleFactor);
      this.dummy.updateMatrix();
      
      this.instancedMesh.setMatrixAt(i, this.dummy.matrix);
      
      // Set individual instance color if supported, or rely on nice shader.
      // ThreeJS InstancedMesh allows setting color per instance
      this.instancedMesh.setColorAt(i, new THREE.Color(p.color));
    }

    // Hide unused instances
    for (let i = this.particles.length; i < this.maxParticles; i++) {
      this.dummy.position.set(0, -999, 0);
      this.dummy.scale.set(0, 0, 0);
      this.dummy.updateMatrix();
      this.instancedMesh.setMatrixAt(i, this.dummy.matrix);
    }

    this.instancedMesh.instanceMatrix.needsUpdate = true;
    if (this.instancedMesh.instanceColor) {
      this.instancedMesh.instanceColor.needsUpdate = true;
    }
  }

  public destroy(scene: THREE.Scene) {
    scene.remove(this.instancedMesh);
    this.instancedMesh.geometry.dispose();
    (this.instancedMesh.material as THREE.Material).dispose();
  }
}
